import { inject, Injectable, Signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ConfigService } from '@app/shared/services/config.service';

export enum LocalStorageKeys {
  over18ApprovalKey = 'over-18-approval',
  redirectUrlAfterOver18 = 'redirect-url-after-over-18',
}

@Injectable({
  providedIn: 'root',
})
export class LocalStorageAsyncService {
  private readonly subjectMap = new Map<
    LocalStorageKeys,
    BehaviorSubject<string>
  >();
  private readonly configService = inject(ConfigService);

  static getStorageValue(key: LocalStorageKeys) {
    return localStorage.getItem(key);
  }

  static getStorageValueWithTimestamp(key: LocalStorageKeys, days: number) {
    const wrapped = localStorage.getItem(key);
    if (!wrapped) {
      return null;
    }
    const { value, timestamp } = JSON.parse(wrapped);
    if (!value || !timestamp || value !== 'true') {
      return null;
    }
    const daysMiliseconds = days * 24 * 60 * 60 * 1000;
    if (timestamp + daysMiliseconds > Date.now()) {
      return value;
    }
    return null;
  }

  static setStorageValue(key: LocalStorageKeys, value: string) {
    localStorage.setItem(key, value);
  }

  static removeStorageValue(key: LocalStorageKeys) {
    localStorage.removeItem(key);
  }

  setStorageValueAsync(key: LocalStorageKeys, value: string) {
    localStorage.setItem(key, value);
    if (!this.subjectMap.get(key)) {
      this.subjectMap.set(key, new BehaviorSubject(value));
      return;
    }
    this.subjectMap.get(key).next(value);
  }

  setStorageValueAsyncWithTimestamp(key: LocalStorageKeys, value: string) {
    localStorage.setItem(key, JSON.stringify({ value, timestamp: Date.now() }));
    if (!this.subjectMap.get(key)) {
      this.subjectMap.set(key, new BehaviorSubject(value));
      return;
    }
    this.subjectMap.get(key).next(value);
  }

  getSubjectForKeyWithTimestamp(key: LocalStorageKeys): Signal<string> {
    const keyDuration =
      key === LocalStorageKeys.over18ApprovalKey
        ? +this.configService.config.over18AcceptDuration
        : 10;
    if (!this.subjectMap.get(key)) {
      this.subjectMap.set(
        key,
        new BehaviorSubject(
          LocalStorageAsyncService.getStorageValueWithTimestamp(
            key,
            keyDuration
          )
        )
      );
    }
    return toSignal(this.subjectMap.get(key).asObservable(), {
      initialValue: LocalStorageAsyncService.getStorageValueWithTimestamp(
        key,
        keyDuration
      ),
    });
  }
}
